import * as Sentry from '@sentry/react';

import { User } from '@/types/User';

import { systemConfig } from './systemConfig';

type InspectTransportOptions = Parameters<typeof Sentry.makeFetchTransport>[0] & {
  maxQueueSize?: number;
  flushAtStartup?: boolean;
};

type SentryEnvelope = Parameters<ReturnType<typeof Sentry.makeFetchTransport>['send']>[0];
type FlushTimeout = Parameters<ReturnType<typeof Sentry.makeFetchTransport>['flush']>[0];

const isErrorEnvelope = (envelope: SentryEnvelope): boolean =>
  (envelope[1] ?? []).some((item) => item[0].type === 'event');

const makeInspectTransport = (options: InspectTransportOptions) => {
  const offline = Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport)(options);
  const direct = Sentry.makeFetchTransport(options);
  return {
    send: (envelope: SentryEnvelope) =>
      isErrorEnvelope(envelope) ? offline.send(envelope) : direct.send(envelope),
    flush: (timeout?: FlushTimeout) =>
      Promise.all([offline.flush(timeout), direct.flush(timeout)]).then((results) =>
        results.every(Boolean)
      ),
  };
};

/** Maps inspect User to Sentry's user context shape. */
export const toSentryUser = (user: User): { id: string; email: string; username: string } => ({
  id: user.id,
  email: user.email,
  username: user.name,
});

//ToDo do not use browser tracing in SW as it is not supported
export const initSentry = (config: { sentry?: { enable: boolean } }, release: string) => {
  if (config?.sentry?.enable) {
    Sentry.init({
      release: release,
      dsn: 'https://5be0102a3073f4fd29280613cfc64dc0@o4506869003976704.ingest.us.sentry.io/4508437618819072', // ToDo: get it from config
      sendDefaultPii: true,
      // integrations: [(new Sentry.BrowserTracing())],
      tracesSampleRate: 1.0, // Adjust this in production
      environment: systemConfig.environment,
      transport: makeInspectTransport,
      transportOptions: {
        maxQueueSize: 30,
        flushAtStartup: true,
      } as InspectTransportOptions,
      integrations: [
        Sentry.replayIntegration({
          // NOTE: This will disable built-in masking. Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: false,
        }),
      ],
      // Session Replay
      replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  } else {
    console.log('Sentry is disabled');
  }
};
